import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, ShoppingBag, CreditCard, Minus, Plus } from 'lucide-react';
import { theme } from '../../utils/theme';
import { useLanguage } from '../../contexts/LanguageContext';

export const CartPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [cartItems, setCartItems] = useState([]);

  useEffect(() => {
    try {
      const items = JSON.parse(localStorage.getItem('sailec_cart') || '[]');
      // Filter out null/undefined items to prevent crashes
      const validItems = Array.isArray(items) 
        ? items.filter(item => item && typeof item === 'object' && item.id) 
        : [];
      setCartItems(validItems);
    } catch (error) {
      console.error('Error loading cart:', error);
      setCartItems([]);
    }
  }, []);

  const removeFromCart = (id) => {
    const newItems = cartItems.filter(item => item.id !== id);
    setCartItems(newItems);
    localStorage.setItem('sailec_cart', JSON.stringify(newItems));
    window.dispatchEvent(new Event('storage')); // Notify other components
  };

  const updateQuantity = (id, newQty) => {
    if (newQty < 1) return;
    const newItems = cartItems.map(item => 
      item.id === id ? { ...item, quantity: newQty } : item
    );
    setCartItems(newItems);
    localStorage.setItem('sailec_cart', JSON.stringify(newItems));
  };

  const subtotal = cartItems.reduce((acc, item) => acc + ((Number(item?.price) || 0) * (item.quantity || 1)), 0);
  const tps = subtotal * 0.05;
  const tvq = subtotal * 0.09975;
  const total = subtotal + tps + tvq;

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen pt-32 pb-12 px-4 flex flex-col items-center justify-center text-center" style={{ backgroundColor: theme.colors.background }}>
        <div className="w-24 h-24 rounded-full bg-gray-800 flex items-center justify-center mb-6">
          <ShoppingBag size={48} className="text-gray-500" />
        </div>
        <h2 className="text-3xl font-bold text-white mb-4">{t.cart?.emptyTitle || "Votre panier est vide"}</h2>
        <p className="text-gray-400 mb-8">{t.cart?.emptyDesc || "Configurez votre filtre idéal dès maintenant."}</p>
        <button 
          onClick={() => navigate('/shop')}
          className="px-8 py-4 rounded-full font-bold text-black transition-transform hover:scale-105"
          style={{ backgroundColor: theme.colors.primary }}
        >
          {t.cart?.goToShop || "Aller à la boutique"}
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-12 px-4" style={{ backgroundColor: theme.colors.background }}>
      <div className="max-w-7xl mx-auto">
        <h1 className="text-4xl font-bold text-white mb-12">{t.cart?.title || "Votre Panier"}</h1>
        
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-6">
            {cartItems.map((item) => (
              <div key={item.id} className="p-6 rounded-2xl border border-white/10 bg-[#111] flex flex-col sm:flex-row gap-6 items-center sm:items-start">
                <div className="w-32 h-32 rounded-xl bg-gray-800 overflow-hidden shrink-0">
                  <img src={item.product?.image} alt={item.product?.name} className="w-full h-full object-cover" />
                </div>
                
                <div className="flex-grow text-center sm:text-left">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="text-xl font-bold text-white">{item.product?.name}</h3>
                    <button 
                      onClick={() => removeFromCart(item.id)}
                      className="text-gray-500 hover:text-red-500 transition-colors p-1"
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>
                  
                  {item.color && <p className="text-gray-400 mb-4">{item.color.name}</p>}
                  
                  {item.dimensions && (
                    <div className="flex flex-wrap gap-4 text-sm text-gray-500 bg-black/30 p-3 rounded-lg">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-gray-300">{t.cart?.width || "L:"}</span> {item.dimensions.width} cm
                      </div>
                      <div className="w-px h-4 bg-white/10"></div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-gray-300">{t.cart?.height || "H:"}</span> {item.dimensions.height} cm
                      </div>
                      <div className="w-px h-4 bg-white/10"></div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-gray-300">{t.cart?.depth || "P:"}</span> {item.dimensions.depth} cm
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-3 bg-white/5 rounded-lg p-1 mt-4 w-fit border border-white/10">
                    <button 
                      onClick={() => updateQuantity(item.id, (item.quantity || 1) - 1)} 
                      className={`p-1 rounded text-white transition-colors ${item.quantity <= 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-white/10'}`}
                      disabled={(item.quantity || 1) <= 1}
                    >
                      <Minus size={16} />
                    </button>
                    <span className="font-bold w-6 text-center text-white">{item.quantity || 1}</span>
                    <button 
                      onClick={() => updateQuantity(item.id, (item.quantity || 1) + 1)} 
                      className="p-1 hover:bg-white/10 rounded text-white transition-colors"
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                </div>
                
                <div className="text-2xl font-bold" style={{ color: theme.colors.primary }}>
                  {((item.price || 0) * (item.quantity || 1)).toFixed(0)}$
                </div>
              </div>
            ))}
          </div>

          {/* Summary */}
          <div className="lg:col-span-1">
            <div className="sticky top-32 p-8 rounded-3xl border border-white/10 bg-[#111]">
              <h3 className="text-2xl font-bold text-white mb-8">{t.cart?.summary || "Résumé"}</h3>
              
              <div className="space-y-4 mb-8">
                <div className="flex justify-between text-gray-400">
                  <span>{t.cart?.subtotal || "Sous-total"}</span>
                  <span>{subtotal.toFixed(2)}$</span>
                </div>
                <div className="flex justify-between text-gray-400">
                  <span>{t.cart?.tps || "TPS (5%)"}</span>
                  <span>{tps.toFixed(2)}$</span>
                </div>
                <div className="flex justify-between text-gray-400">
                  <span>{t.cart?.tvq || "TVQ (9.975%)"}</span>
                  <span>{tvq.toFixed(2)}$</span>
                </div>
                <div className="h-px bg-white/10 my-4"></div>
                <div className="flex justify-between text-xl font-bold text-white">
                  <span>{t.cart?.total || "Total"}</span>
                  <span style={{ color: theme.colors.primary }}>{total.toFixed(2)}$</span>
                </div>
              </div>

              <button 
                onClick={() => navigate('/checkout')}
                className="relative w-full py-4 rounded-full font-bold text-black flex items-center justify-center gap-2 transition-all hover:scale-105 overflow-hidden group"
                style={{ 
                  background: theme.gradients.primary,
                  boxShadow: `0 0 30px -10px ${theme.colors.primary}60`
                }}
              >
                <span className="relative z-10 flex items-center gap-2">
                  <CreditCard size={20} />
                  {t.cart?.checkout || "Paiement"}
                </span>
                <div className="absolute top-0 left-0 w-full h-full bg-white opacity-20 transform -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};